// CRM-LEADS-2026-10-16 §7: the bar over the Leads list once leads are selected -- Copy list, Export CSV, Text all selected.
// Texting goes one at a time through the staff-text route (STOP and quiet hours per person); at most 25 per batch.
// NIGHT-DIRECTSTAFF-2026-10-17 §1: merge fields, a live preview for the first recipient, and a warning before sending
// when a selected lead is missing a field the message uses (send with it blank, or deselect them).
import { useEffect, useRef, useState } from 'react';
import { toTSV, toCSV, csvFileName, sendBatch, batchSummary, BATCH_MAX, PACE_MS, MERGE_FIELDS, mergeFor, missingFields,
  unknownFields } from './lib/gather';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700,
  border: 0, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 });
const DEFAULT_BODY = 'Hi {first_name}, this is ProTherapy Staffing. ';
const listNames = (rows) => rows.map((r) => r.name || String(r.id).slice(0, 8)).join(', ');

/**
 * selected: the selected leads that are on the filtered list now, in list order. send(leadId, body, anyway) = staff.textLead.
 * onDeselect(ids): take those leads out of the selection (the "Deselect those" choice); without it, that choice is hidden.
 */
export default function GatherBar({ selected, send, toast = () => {}, download = null, clipboard = null, pauseMs = PACE_MS, onDeselect = null }) {
  const [composing, setComposing] = useState(false);
  const [body, setBodyRaw] = useState(DEFAULT_BODY);
  const [anyway, setAnyway] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [asking, setAsking] = useState(false);   // the missing-field warning is open
  const [ranFor, setRanFor] = useState(0);       // how many the last batch meant to text
  const stopRef = useRef(false);
  const boxRef = useRef(null);
  // A new selection or a new message starts clean: the last batch's results belong to the last batch.
  const idsKey = selected.slice(0, BATCH_MAX).map((l) => l.id).join(',');
  useEffect(() => { if (!running) { setResults([]); setAsking(false); } }, [idsKey]);
  if (!selected.length) return null;
  const tooMany = selected.length > BATCH_MAX;
  const recipients = selected.slice(0, BATCH_MAX);
  const byId = Object.fromEntries(selected.map((l) => [l.id, l]));
  const missing = missingFields(body, recipients);
  const unknown = unknownFields(body);
  const setBody = (v) => { setBodyRaw(v); setAsking(false); if (!running) setResults([]); };

  const insert = (key) => {
    const tok = `{${key}}`;
    const el = boxRef.current;
    const at = el && typeof el.selectionStart === 'number' ? el.selectionStart : body.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : at;
    setBody(body.slice(0, at) + tok + body.slice(end));
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(at + tok.length, at + tok.length); } }, 0);
  };
  const copy = async () => {
    try { await (clipboard || navigator.clipboard).writeText(toTSV(selected)); toast(`Copied ${selected.length} lead${selected.length === 1 ? '' : 's'} as a table`); }
    catch { toast('Copy failed: try Export CSV'); }
  };
  const exportCsv = () => {
    const name = csvFileName();
    if (download) { download(name, toCSV(selected)); return; }
    const url = URL.createObjectURL(new Blob([toCSV(selected)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const run = async () => {
    if (running || !body.trim() || unknown.length) return;
    setAsking(false);
    stopRef.current = false;
    setRunning(true); setResults([]); setRanFor(recipients.length);
    const out = await sendBatch({ recipients, body, sendAnyway: anyway, send, pauseMs, shouldStop: () => stopRef.current,
      onEach: (r) => setResults((x) => [...x, r]) });
    setRunning(false);
    toast(batchSummary(out, recipients.length));
  };
  const clickSend = () => { if (missing.length) setAsking(true); else run(); };
  const blocked = running || !body.trim() || unknown.length > 0;

  return (
    <div data-testid="gather-bar" className="card" style={{ padding: 12, marginBottom: 12, background: '#f8fafc' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span data-testid="gather-count" style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{selected.length} selected</span>
        <button className="nb" data-testid="gather-copy" onClick={copy} style={btn('#e2e8f0', '#0f172a')}>Copy list</button>
        <button className="nb" data-testid="gather-csv" onClick={exportCsv} style={btn('#e2e8f0', '#0f172a')}>Export CSV</button>
        <button className="nb" data-testid="gather-text" onClick={() => setComposing((v) => !v)} aria-expanded={composing}
          style={btn('#0f766e', 'white')}>Text all selected</button>
      </div>
      {composing && (
        <div data-testid="gather-compose" style={{ marginTop: 12 }}>
          <p style={{ fontSize: 12, color: '#334155', margin: '0 0 6px' }}>
            One text to each, sent one at a time from the business line, about 7 seconds apart so the texting limit isn't hit
            ({recipients.length > 1 ? `${recipients.length} take about ${Math.max(1, Math.round(((recipients.length - 1) * pauseMs) / 60000))} min` : 'one text'}; keep this page open).
            Anyone who replied STOP is skipped, and anyone outside 8am-8pm their time is held back unless you tick "Send anyway".
            Each send shows in that lead's conversation.</p>
          {tooMany && <p data-testid="gather-cap" style={{ fontSize: 12, color: '#92400e', margin: '0 0 6px' }}>
            {BATCH_MAX} at a time: this batch texts the first {BATCH_MAX} of the {selected.length} selected.</p>}
          <label htmlFor="gather-body" style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>Message</label>
          <textarea id="gather-body" ref={boxRef} data-testid="gather-body" className="input" rows={4} value={body} disabled={running}
            onChange={(e) => setBody(e.target.value)} style={{ marginTop: 4, fontSize: 14 }} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '6px 0' }}>
            <span style={{ fontSize: 12, color: '#475569' }}>Insert:</span>
            {Object.entries(MERGE_FIELDS).map(([k, f]) => (
              <button key={k} type="button" className="nb" data-testid={`gather-field-${k}`} disabled={running} onClick={() => insert(k)} title={`Insert the ${f.label}`}
                style={btn('#e0f2fe', '#075985', running)}>{`{${k}}`}</button>
            ))}
          </div>
          {unknown.length > 0 && <p data-testid="gather-unknown" role="alert" style={{ fontSize: 12, color: '#b91c1c', margin: '0 0 6px' }}>
            {unknown.join(', ')} {unknown.length === 1 ? "isn't a merge field" : "aren't merge fields"}: use {'{first_name}'}, {'{discipline}'} or {'{state}'}.</p>}
          <div data-testid="gather-preview" style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', background: 'white', margin: '0 0 6px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>
              Preview for {(recipients[0] && recipients[0].name) || 'the first lead'}</div>
            <div data-testid="gather-preview-text" style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {mergeFor(body, recipients[0]) || <span style={{ color: '#64748b' }}>(empty)</span>}</div>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44, fontSize: 13 }}>
            <input type="checkbox" data-testid="gather-anyway" checked={anyway} disabled={running} onChange={(e) => setAnyway(e.target.checked)} style={{ width: 18, height: 18 }} />
            Send anyway, even outside 8am-8pm their time
          </label>
          {asking && missing.length > 0 && (
            <div data-testid="gather-missing" role="alert" style={{ border: '1px solid #f59e0b', background: '#fffbeb', borderRadius: 8, padding: 10, margin: '0 0 8px' }}>
              <p style={{ fontSize: 13, color: '#78350f', margin: '0 0 6px' }}>
                {missing.length} of the {recipients.length} {missing.length === 1 ? 'has' : 'have'} no {
                  [...new Set(missing.flatMap((m) => m.missing))].map((k) => MERGE_FIELDS[k].label).join(' or ')}: {listNames(missing)}.</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="nb" data-testid="gather-missing-blank" onClick={run} style={btn('#0f766e', 'white')}>Send anyway, leaving it blank</button>
                {onDeselect && <button className="nb" data-testid="gather-missing-deselect" style={btn('#e2e8f0', '#0f172a')}
                  onClick={() => { onDeselect(missing.map((m) => m.id)); setAsking(false); }}>Deselect {missing.length === 1 ? 'that lead' : `those ${missing.length}`}</button>}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="nb" data-testid="gather-send" onClick={clickSend} disabled={blocked} style={btn('#0f766e', 'white', blocked)}>
              {running ? `Sending ${results.length + 1} of ${recipients.length}…` : `Text ${recipients.length}`}</button>
            {running && <button className="nb" data-testid="gather-stop" onClick={() => { stopRef.current = true; }} style={btn('#fee2e2', '#b91c1c')}>Stop</button>}
          </div>
          {results.length > 0 && (
            <ul data-testid="gather-results" style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', fontSize: 13 }}>
              {results.map((r) => (
                <li key={r.id} data-testid={`gather-result-${String(r.id).slice(0, 8)}`} style={{ padding: '3px 0', color: r.ok ? '#166534' : '#b91c1c', overflowWrap: 'anywhere' }}>
                  {(byId[r.id] && (byId[r.id].name || String(r.id).slice(0, 8))) || String(r.id).slice(0, 8)}: {r.ok ? (r.duplicate ? 'already sent, not sent twice' : 'sent') : r.message}
                </li>
              ))}
            </ul>
          )}
          {!running && results.length > 0 && <p data-testid="gather-summary" role="status" style={{ fontSize: 13, fontWeight: 600, margin: '8px 0 0' }}>{batchSummary(results, ranFor)}</p>}
        </div>
      )}
    </div>
  );
}
